import React, { useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  FileInput,
  Group,
  Loader,
  Modal,
  Paper,
  SegmentedControl,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { useTranslation } from 'react-i18next';
import {
  IconAlertCircle,
  IconCheck,
  IconEdit,
  IconEye,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import { useGetEICFTemplatesByFacility } from '../../../global/entities/EICFTemplates/queries/useGetEICFTemplatesByFacility.tsx';
import { useGetEICFTemplateForFacility } from '../../../global/entities/EICFTemplates/queries/useGetEICFTemplateForFacility.tsx';
import { usePostEICFTemplate } from '../../../global/entities/EICFTemplates/mutations/usePostEICFTemplate.tsx';
import { usePutEICFTemplate } from '../../../global/entities/EICFTemplates/mutations/usePutEICFTemplate.tsx';
import { useApproveEICFTemplate } from '../../../global/entities/EICFTemplates/mutations/useApproveEICFTemplate.tsx';
import { useAssignEICFTemplate } from '../../../global/entities/EICFTemplates/mutations/useAssignEICFTemplate.tsx';
import { useUnassignEICFTemplate } from '../../../global/entities/EICFTemplates/mutations/useUnassignEICFTemplate.tsx';
import { useDeleteEICFTemplate } from '../../../global/entities/EICFTemplates/mutations/useDeleteEICFTemplate.tsx';
import {
  EICFTemplateKind,
  EICFTemplateStatus,
  IEICFTemplate,
  IEICFTemplateInput,
} from '../../../global/entities/EICFTemplates/EICFTemplate.ts';
import { EICFTemplatesApi } from '../../../global/apis/EICFApi/EICFTemplatesApi.ts';
import { uploadFileToS3 } from '../../../global/helpers/uploadFileToS3.ts';
import {
  hasSignatureField,
  listFields,
  PdfFieldInfo,
} from '../../../global/helpers/eicfFillablePdf.ts';
import {
  defaultRuleForType,
  EICF_FIELD_ROLES,
  FieldConfigMap,
  parseFieldConfig,
  serializeFieldConfig,
} from '../../../global/entities/EICFTemplates/fieldConfig.ts';
import { AppMedError } from '../../../global/error/AppMedError.ts';
import {
  TableContextProvider,
  Th,
  useTable,
} from '../../../global/hooks/useTable.tsx';
import HcfEICFEscalationPanel from './HcfEICFEscalationPanel.tsx';

interface HcfEICFTemplatesPanelProps {
  facilityId: number;
}

const emptyInput = (facilityId: number): IEICFTemplateInput => ({
  healthcareFacilityID: facilityId,
  code: '',
  nameEn: '',
  nameFr: '',
  descriptionEn: '',
  descriptionFr: '',
  rawJson: '',
  themeJson: '',
  kind: EICFTemplateKind.SurveyJson,
  documentS3Key: null,
});

const kindLabelKey = (kind: string): string =>
  kind === EICFTemplateKind.FillablePdf
    ? 'eicf.admin.kindFillablePdf'
    : 'eicf.admin.kindSurveyJson';

const documentFilename = (documentS3Key?: string | null): string =>
  documentS3Key ? documentS3Key.split('/').pop() ?? documentS3Key : '';

const HcfEICFTemplatesPanel: React.FC<HcfEICFTemplatesPanelProps> = ({
  facilityId,
}) => {
  return (
    <TableContextProvider>
      <PanelInner facilityId={facilityId} />
    </TableContextProvider>
  );
};

const PanelInner: React.FC<HcfEICFTemplatesPanelProps> = ({ facilityId }) => {
  const { t } = useTranslation();
  const { data: templates, isLoading } =
    useGetEICFTemplatesByFacility(facilityId);
  const { data: activeTemplate } = useGetEICFTemplateForFacility(facilityId);
  const assign = useAssignEICFTemplate();
  const unassign = useUnassignEICFTemplate();
  const deleteTemplate = useDeleteEICFTemplate();

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<IEICFTemplate | null>(
    null,
  );

  const {
    setSorting,
    sortBy,
    reverseSortDirection,
    setSortedData,
    sortedData,
  } = useTable();

  useEffect(() => {
    setSortedData(templates || []);
  }, [templates, setSortedData]);

  const rows = useMemo(
    () => (sortedData as IEICFTemplate[]) || [],
    [sortedData],
  );

  const openCreate = () => {
    setEditingTemplate(null);
    setEditorOpen(true);
  };

  const openEdit = (tpl: IEICFTemplate) => {
    setEditingTemplate(tpl);
    setEditorOpen(true);
  };

  return (
    <Paper shadow="sm" radius="md" p="md">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <div>
            <Title order={3}>{t('eicf.admin.facilityTabTitle')}</Title>
            <Text size="sm" c="dimmed">
              {t('eicf.admin.facilityTabSubtitle')}
            </Text>
          </div>
          <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
            {t('eicf.admin.newTemplate')}
          </Button>
        </Group>

        <Card withBorder padding="md" radius="md">
          <Group justify="space-between" align="center">
            <div>
              <Text fw={600}>{t('eicf.admin.activeTemplateLabel')}</Text>
              {activeTemplate ? (
                <Text size="sm">
                  {activeTemplate.code} v{activeTemplate.version} —{' '}
                  {activeTemplate.nameEn}
                </Text>
              ) : (
                <Text size="sm" c="dimmed">
                  {t('eicf.admin.noActiveTemplate')}
                </Text>
              )}
            </div>
            {activeTemplate && (
              <Button
                variant="outline"
                color="red"
                onClick={() => unassign.mutate(facilityId)}
                loading={unassign.isPending}
              >
                {t('eicf.admin.unassign')}
              </Button>
            )}
          </Group>
        </Card>

        <HcfEICFEscalationPanel facilityId={facilityId} />

        {isLoading ? (
          <Loader />
        ) : (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Th
                  sorted={sortBy === 'code'}
                  reversed={reverseSortDirection}
                  onSort={() => setSorting('code', templates || [])}
                >
                  {t('eicf.admin.code')}
                </Th>
                <Th
                  sorted={sortBy === 'version'}
                  reversed={reverseSortDirection}
                  onSort={() => setSorting('version', templates || [])}
                >
                  {t('eicf.admin.version')}
                </Th>
                <Th
                  sorted={sortBy === 'nameEn'}
                  reversed={reverseSortDirection}
                  onSort={() => setSorting('nameEn', templates || [])}
                >
                  {t('eicf.admin.nameEn')}
                </Th>
                <Th
                  sorted={sortBy === 'kind'}
                  reversed={reverseSortDirection}
                  onSort={() => setSorting('kind', templates || [])}
                >
                  {t('eicf.admin.type')}
                </Th>
                <Th
                  sorted={sortBy === 'status'}
                  reversed={reverseSortDirection}
                  onSort={() => setSorting('status', templates || [])}
                >
                  {t('eicf.admin.status')}
                </Th>
                <Th
                  sorted={sortBy === 'lastModifiedDate'}
                  reversed={reverseSortDirection}
                  onSort={() => setSorting('lastModifiedDate', templates || [])}
                >
                  {t('eicf.admin.lastModified')}
                </Th>
                <Table.Th>{t('eicf.admin.actions')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={7}>
                    <Text c="dimmed" ta="center">
                      {t('eicf.admin.noTemplates')}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                rows.map((tpl) => {
                  const isActive = activeTemplate?.id === tpl.id;
                  return (
                    <Table.Tr key={tpl.id}>
                      <Table.Td>{tpl.code}</Table.Td>
                      <Table.Td>{tpl.version}</Table.Td>
                      <Table.Td>{tpl.nameEn}</Table.Td>
                      <Table.Td>
                        <Badge variant="light" color="grape">
                          {t(kindLabelKey(tpl.kind))}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Badge
                          color={
                            tpl.status === EICFTemplateStatus.Approved
                              ? 'green'
                              : 'gray'
                          }
                        >
                          {tpl.status}
                        </Badge>
                        {isActive && (
                          <Badge ml="xs" color="blue">
                            {t('eicf.admin.activeBadge')}
                          </Badge>
                        )}
                      </Table.Td>
                      <Table.Td>
                        {new Date(tpl.lastModifiedDate).toLocaleDateString()}
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs">
                          <Tooltip
                            label={
                              tpl.status === EICFTemplateStatus.Draft
                                ? t('eicf.admin.edit')
                                : t('eicf.admin.view')
                            }
                          >
                            <ActionIcon
                              variant="subtle"
                              color="blue"
                              aria-label={
                                tpl.status === EICFTemplateStatus.Draft
                                  ? t('eicf.admin.edit')
                                  : t('eicf.admin.view')
                              }
                              onClick={() => openEdit(tpl)}
                            >
                              {tpl.status === EICFTemplateStatus.Draft ? (
                                <IconEdit size={18} />
                              ) : (
                                <IconEye size={18} />
                              )}
                            </ActionIcon>
                          </Tooltip>
                          {tpl.status === EICFTemplateStatus.Approved &&
                          !isActive ? (
                            <Tooltip label={t('eicf.admin.setActive')}>
                              <ActionIcon
                                variant="subtle"
                                color="blue"
                                onClick={() =>
                                  assign.mutate({
                                    healthcareFacilityID: facilityId,
                                    eicfTemplateID: tpl.id,
                                  })
                                }
                                loading={assign.isPending}
                              >
                                <IconCheck size={18} />
                              </ActionIcon>
                            </Tooltip>
                          ) : (
                            <Tooltip
                              label={t('eicf.admin.activeTemplateTooltip')}
                            >
                              <ActionIcon
                                variant="subtle"
                                color="green"
                                style={{ cursor: 'default' }}
                                aria-hidden
                              >
                                <IconCheck size={18} />
                              </ActionIcon>
                            </Tooltip>
                          )}
                          {!isActive ? (
                            <Tooltip label={t('eicf.admin.delete')}>
                              <ActionIcon
                                variant="subtle"
                                color="red"
                                onClick={() =>
                                  modals.openConfirmModal({
                                    title: t('eicf.admin.deleteConfirmTitle'),
                                    children: (
                                      <Text size="sm">
                                        {t('eicf.admin.deleteConfirmBody', {
                                          code: tpl.code,
                                          version: tpl.version,
                                        })}
                                      </Text>
                                    ),
                                    labels: {
                                      confirm: t('eicf.admin.delete'),
                                      cancel: t('global.back'),
                                    },
                                    confirmProps: { color: 'red' },
                                    onConfirm: () =>
                                      deleteTemplate.mutate(tpl.id),
                                  })
                                }
                              >
                                <IconTrash size={18} />
                              </ActionIcon>
                            </Tooltip>
                          ) : (
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              disabled
                              aria-hidden
                            >
                              <IconTrash size={18} />
                            </ActionIcon>
                          )}
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  );
                })
              )}
            </Table.Tbody>
          </Table>
        )}
      </Stack>

      <TemplateEditorModal
        opened={editorOpen}
        onClose={() => setEditorOpen(false)}
        facilityId={facilityId}
        template={editingTemplate}
      />
    </Paper>
  );
};

interface TemplateEditorModalProps {
  opened: boolean;
  onClose: () => void;
  facilityId: number;
  template: IEICFTemplate | null;
}

const TemplateEditorModal: React.FC<TemplateEditorModalProps> = ({
  opened,
  onClose,
  facilityId,
  template,
}) => {
  const { t } = useTranslation();
  const post = usePostEICFTemplate();
  const put = usePutEICFTemplate();
  const approve = useApproveEICFTemplate();

  const [form, setForm] = useState<IEICFTemplateInput>(emptyInput(facilityId));
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<PdfFieldInfo[]>([]);
  const [fieldConfig, setFieldConfig] = useState<FieldConfigMap>({});

  useEffect(() => {
    if (!opened) return;
    setError(null);
    setFile(null);
    if (template) {
      setForm({
        healthcareFacilityID: template.healthcareFacilityID,
        code: template.code,
        nameEn: template.nameEn,
        nameFr: template.nameFr,
        descriptionEn: template.descriptionEn ?? '',
        descriptionFr: template.descriptionFr ?? '',
        rawJson: template.rawJson,
        themeJson: template.themeJson ?? '',
        kind: template.kind,
        documentS3Key: template.documentS3Key ?? null,
        fieldConfigJson: template.fieldConfigJson ?? null,
      });
      const cfg = parseFieldConfig(template.fieldConfigJson);
      setFieldConfig(cfg);
      setFields(
        Object.keys(cfg).map((name) => ({
          name,
          type: 'unknown' as const,
          required: false,
        })),
      );
    } else {
      setForm(emptyInput(facilityId));
      setFieldConfig({});
      setFields([]);
    }
  }, [opened, template, facilityId]);

  // When a PDF is chosen, list its fields and seed sensible default roles.
  const handleFileSelected = async (selected: File | null) => {
    setFile(selected);
    if (!selected) return;
    try {
      const list = await listFields(await selected.arrayBuffer());
      setFields(list);
      setFieldConfig((prev) => {
        const next: FieldConfigMap = {};
        list.forEach((f) => {
          next[f.name] = prev[f.name] ?? defaultRuleForType(f.type, f.name);
        });
        return next;
      });
    } catch {
      setError(t('eicf.admin.documentParseError'));
    }
  };

  const setFieldRule = (
    name: string,
    patch: Partial<FieldConfigMap[string]>,
  ) =>
    setFieldConfig((prev) => ({
      ...prev,
      [name]: { ...prev[name], ...patch },
    }));

  const isNew = !template;
  const isLocked = !isNew && template?.status === EICFTemplateStatus.Approved;
  const canApprove = !isNew && template?.canApprove;
  const isFillablePdf = form.kind === EICFTemplateKind.FillablePdf;

  const handleSave = async () => {
    setError(null);
    try {
      let input = form;
      if (isFillablePdf)
        {input = {
          ...input,
          fieldConfigJson: serializeFieldConfig(fieldConfig),
        };}
      if (isNew && isFillablePdf) {
        if (!file) {
          setError(t('eicf.admin.documentRequired'));
          return;
        }
        // The patient needs somewhere to sign; refuse a PDF with no signature
        // field rather than create a template no one can ever sign.
        if (!hasSignatureField(fields)) {
          setError(t('eicf.admin.signatureFieldRequired'));
          return;
        }
        setUploading(true);
        const policy = await EICFTemplatesApi.getDocumentUploadUrl(
          facilityId,
          encodeURIComponent(file.name),
        );
        await uploadFileToS3(policy, file);
        input = { ...input, rawJson: '', documentS3Key: policy.key };
      }

      if (isNew)
        await post.mutateAsync(input);
       else if (template)
        await put.mutateAsync({ id: template.id, input });

      onClose();
    } catch (e) {
      if (e instanceof AppMedError) setError(e.message);
      else if (e instanceof Error) setError(e.message);
      else setError(t('eicf.admin.unexpectedError'));
    } finally {
      setUploading(false);
    }
  };

  const handleApprove = async () => {
    if (!template) return;
    setError(null);
    try {
      await approve.mutateAsync(template.id);
      onClose();
    } catch (e) {
      if (e instanceof AppMedError) setError(e.message);
      else setError(t('eicf.admin.unexpectedError'));
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="xl"
      title={
        <Group>
          <Title order={4}>
            {isNew
              ? t('eicf.admin.newTemplate')
              : `${template?.code} v${template?.version}`}
          </Title>
          {template && (
            <Badge
              color={
                template.status === EICFTemplateStatus.Approved
                  ? 'green'
                  : 'gray'
              }
            >
              {template.status}
            </Badge>
          )}
        </Group>
      }
    >
      <Stack gap="md">
        {error && (
          <Alert
            icon={<IconAlertCircle size="1rem" />}
            color="red"
            title={t('eicf.page.errorTitle')}
          >
            {error}
          </Alert>
        )}
        {isLocked && (
          <Alert
            color="blue"
            icon={<IconAlertCircle size="1rem" />}
            title={t('eicf.admin.lockedTitle')}
          >
            {t('eicf.admin.lockedBody')}
          </Alert>
        )}
        <div>
          <Text size="sm" fw={500}>
            {t('eicf.admin.type')}
          </Text>
          <SegmentedControl
            mt={4}
            fullWidth
            value={form.kind}
            onChange={(value) => setForm({ ...form, kind: value })}
            disabled={!isNew || isLocked}
            data={[
              {
                label: t('eicf.admin.kindSurveyJson'),
                value: EICFTemplateKind.SurveyJson,
              },
              {
                label: t('eicf.admin.kindFillablePdf'),
                value: EICFTemplateKind.FillablePdf,
              },
            ]}
          />
        </div>
        <TextInput
          label={t('eicf.admin.code')}
          description={t('eicf.admin.codeHelp')}
          value={form.code}
          onChange={(e) => setForm({ ...form, code: e.currentTarget.value })}
          disabled={!isNew || isLocked}
          required
        />
        <Group grow>
          <TextInput
            label={t('eicf.admin.nameEn')}
            value={form.nameEn}
            onChange={(e) =>
              setForm({ ...form, nameEn: e.currentTarget.value })
            }
            disabled={isLocked}
            required
          />
          <TextInput
            label={t('eicf.admin.nameFr')}
            value={form.nameFr}
            onChange={(e) =>
              setForm({ ...form, nameFr: e.currentTarget.value })
            }
            disabled={isLocked}
            required
          />
        </Group>
        <Group grow>
          <Textarea
            label={t('eicf.admin.descriptionEn')}
            value={form.descriptionEn ?? ''}
            onChange={(e) =>
              setForm({ ...form, descriptionEn: e.currentTarget.value })
            }
            autosize
            minRows={2}
            disabled={isLocked}
          />
          <Textarea
            label={t('eicf.admin.descriptionFr')}
            value={form.descriptionFr ?? ''}
            onChange={(e) =>
              setForm({ ...form, descriptionFr: e.currentTarget.value })
            }
            autosize
            minRows={2}
            disabled={isLocked}
          />
        </Group>
        {isFillablePdf ? (
          isNew ? (
            <FileInput
              label={t('eicf.admin.documentLabel')}
              description={t('eicf.admin.documentHelp')}
              placeholder={t('eicf.admin.documentPlaceholder')}
              accept="application/pdf"
              value={file}
              onChange={handleFileSelected}
              clearable
              required
            />
          ) : (
            <TextInput
              label={t('eicf.admin.documentLabel')}
              value={documentFilename(form.documentS3Key)}
              readOnly
              disabled
            />
          )
        ) : (
          <Textarea
            label={t('eicf.admin.rawJson')}
            description={t('eicf.admin.rawJsonHelp')}
            value={form.rawJson}
            onChange={(e) =>
              setForm({ ...form, rawJson: e.currentTarget.value })
            }
            autosize
            minRows={10}
            maxRows={30}
            disabled={isLocked}
            required
            styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }}
          />
        )}
        {isFillablePdf && fields.length > 0 && (
          <Stack gap="xs">
            <div>
              <Text fw={600}>{t('eicf.admin.fieldRolesTitle')}</Text>
              <Text size="xs" c="dimmed">
                {t('eicf.admin.fieldRolesHelp')}
              </Text>
            </div>
            <Table striped withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('eicf.admin.fieldName')}</Table.Th>
                  <Table.Th>{t('eicf.admin.fieldType')}</Table.Th>
                  <Table.Th>{t('eicf.admin.fieldRole')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {fields.map((f) => {
                  const rule =
                    fieldConfig[f.name] ?? defaultRuleForType(f.type, f.name);
                  return (
                    <Table.Tr key={f.name}>
                      <Table.Td>{f.name}</Table.Td>
                      <Table.Td>{f.type}</Table.Td>
                      <Table.Td>
                        <Select
                          aria-label={t('eicf.admin.fieldRole')}
                          value={rule.role}
                          onChange={(v) =>
                            setFieldRule(f.name, {
                              role:
                                (v as FieldConfigMap[string]['role']) ??
                                EICF_FIELD_ROLES.Patient,
                            })
                          }
                          disabled={isLocked}
                          data={[
                            {
                              value: EICF_FIELD_ROLES.Patient,
                              label: t('eicf.admin.rolePatient'),
                            },
                            {
                              value: EICF_FIELD_ROLES.Hcp,
                              label: t('eicf.admin.roleHcp'),
                            },
                            {
                              value: EICF_FIELD_ROLES.Display,
                              label: t('eicf.admin.roleDisplay'),
                            },
                          ]}
                        />
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Stack>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {t('global.back')}
          </Button>
          {!isLocked && (
            <Button
              onClick={handleSave}
              loading={post.isPending || put.isPending || uploading}
            >
              {t('eicf.admin.save')}
            </Button>
          )}
          {canApprove && (
            <Button
              color="green"
              onClick={handleApprove}
              loading={approve.isPending}
            >
              {t('eicf.admin.approve')}
            </Button>
          )}
        </Group>
        {template?.approvedDate && (
          <Text size="sm" c="dimmed">
            {t('eicf.admin.approvedOn', {
              date: new Date(template.approvedDate).toLocaleString(),
            })}
          </Text>
        )}
      </Stack>
    </Modal>
  );
};

export default HcfEICFTemplatesPanel;
